import type { Flow, FlowNode, Run, RunEvent, Artifact, HumanOversightMode } from '../types';
import { api, ApiError } from './apiClient';
import { normalizeRun, normalizeRunList } from './normalizers';
// `delay`/`clone` permanecem como utilitários da simulação client-side (animação
// por nó + isolamento do objeto retonado) — não são mais dados mock.
import { delay, clone } from './_mock';
import { uid, nowIso } from '../lib/utils';
import { computeLoopBody } from '../lib/loopLayout';
import { estimateNodeCost } from '../lib/cost';

export type HumanDecision = 'approve' | 'reject' | 'edit';

export interface SimulateControls {
  // Emite cada evento gradualmente (log streaming + destaque no canvas).
  onEvent: (event: RunEvent) => void;
  // HITL: chamado ao atingir um nó de revisão; deve resolver com a decisão humana.
  requestHumanDecision?: (node: FlowNode, event: RunEvent) => Promise<HumanDecision>;
  // HOTL: controles de intervenção sempre disponíveis durante o run.
  control?: {
    aborted: () => boolean;
    waitWhilePaused: () => Promise<void>;
  };
  stepMs?: number;
}

// Mensagem mock plausível por tipo de nó.
function nodeMessage(node: FlowNode): string {
  switch (node.kind) {
    case 'trigger':
      return 'Gatilho recebido. Payload validado.';
    case 'agent':
      return 'Pensando… consultando ferramentas e formulando resposta.';
    case 'llm':
      return `Chamando modelo ${(node.data.model as string) ?? 'LLM'}…`;
    case 'tool': {
      const t = node.data.tool as string;
      if (t === 'rag') return 'Recuperados 5 documentos relevantes.';
      if (t === 'http') return 'Requisição HTTP concluída (200 OK).';
      if (t === 'sql') return 'Query executada — 128 linhas retornadas.';
      return `Ferramenta "${t ?? 'tool'}" executada.`;
    }
    case 'router':
      return 'Avaliando condições e escolhendo ramo de saída.';
    case 'loop':
      return 'Entrando no corpo do loop.';
    case 'memory':
      return `${node.data.operation === 'write' ? 'Gravando' : 'Lendo'} memória (${(node.data.scope as string) ?? 'short'}).`;
    case 'kb':
      return `Busca na base (top-${(node.data.topK as number) ?? 5}).`;
    case 'human_review':
      return 'Ponto de revisão humana.';
    case 'output':
      return `Saída (${(node.data.format as string) ?? 'text'}) entregue ao destino.`;
    default:
      return 'Nó executado.';
  }
}

function effectiveMode(flow: Flow, node: FlowNode): HumanOversightMode {
  return node.oversightOverride ?? flow.oversightMode;
}

// Topo-sort ignorando arestas de loopback (Kahn). Mantém ordem estável dos nós.
function topoOrder(flow: Flow): FlowNode[] {
  const forward = flow.edges.filter((e) => !e.isLoopBack);
  const indeg = new Map<string, number>();
  flow.nodes.forEach((n) => indeg.set(n.id, 0));
  forward.forEach((e) => indeg.set(e.target, (indeg.get(e.target) ?? 0) + 1));

  const queue = flow.nodes.filter((n) => (indeg.get(n.id) ?? 0) === 0).map((n) => n.id);
  const order: string[] = [];
  while (queue.length) {
    const id = queue.shift()!;
    order.push(id);
    forward
      .filter((e) => e.source === id)
      .forEach((e) => {
        const d = (indeg.get(e.target) ?? 0) - 1;
        indeg.set(e.target, d);
        if (d === 0) queue.push(e.target);
      });
  }
  // Anexa nós não alcançados (ciclos) preservando a ordem original.
  flow.nodes.forEach((n) => {
    if (!order.includes(n.id)) order.push(n.id);
  });
  const byId = new Map(flow.nodes.map((n) => [n.id, n]));
  return order.map((id) => byId.get(id)!).filter(Boolean);
}

export const runsService = {
  async list(): Promise<Run[]> {
    const res = await api.get<{ runs: unknown[] }>('/runs');
    return normalizeRunList(res.runs);
  },

  async get(id: string): Promise<Run | undefined> {
    try {
      const res = await api.get<{ run: unknown }>(`/runs/${encodeURIComponent(id)}`);
      return normalizeRun(res.run);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return undefined;
      throw err;
    }
  },

  // Execução REAL do fluxo (encadeada) no backend. Retona o Run completo
  // (ou pausado em human_review). Envia o DTO do canvas para rodar o que está montado.
  async execute(flow: Flow, input: string): Promise<Run> {
    const res = await api.post<{ run: unknown }>(`/flows/${encodeURIComponent(flow.id)}/execute`, { input, flow });
    return normalizeRun(res.run);
  },

  // Simula a execução de um fluxo, emitindo RunEvents gradualmente. Respeita o modo de supervisão.
  async simulate(flow: Flow, controls: SimulateControls): Promise<Run> {
    const stepMs = controls.stepMs ?? 650;
    const startedAt = nowIso();
    const events: RunEvent[] = [];
    const runId = uid('run');

    const emit = (e: Omit<RunEvent, 'timestamp'>) => {
      const full: RunEvent = { ...e, timestamp: nowIso() };
      events.push(full);
      controls.onEvent(full);
    };

    const aborted = () => controls.control?.aborted() ?? false;
    const pauseGate = async () => {
      if (controls.control) await controls.control.waitWhilePaused();
    };

    const order = topoOrder(flow);
    const handled = new Set<string>();
    const byId = new Map(flow.nodes.map((n) => [n.id, n]));

    // --- Controle de custo (limite por execução e mensal) ---
    const cc = flow.costControl;
    let runCost = 0;
    let costWaned = false;
    let costApproved = false;

    // Acumula o custo do nó e aplica a política ao estourar o teto.
    async function chargeAndEnforce(node: FlowNode, iteration?: number): Promise<'ok' | 'blocked'> {
      runCost = Math.round((runCost + estimateNodeCost(node.kind)) * 100) / 100;
      if (!cc) return 'ok';
      const overRun = cc.perRunCapBRL > 0 && runCost > cc.perRunCapBRL;
      const overMonth = cc.monthlyCapBRL > 0 && cc.spentBRL + runCost > cc.monthlyCapBRL;
      if (!overRun && !overMonth) return 'ok';
      if (costApproved) return 'ok';

      const reason = overRun
        ? `Custo da execução (R$ ${runCost.toFixed(2)}) excedeu o teto por execução (R$ ${cc.perRunCapBRL}).`
        : `Custo (R$ ${(cc.spentBRL + runCost).toFixed(2)}) excederia o teto mensal (R$ ${cc.monthlyCapBRL}).`;

      if (cc.onExceed === 'warn') {
        if (!costWaned) {
          costWaned = true;
          emit({ nodeId: node.id, iteration, status: 'running', message: `⚠️ ${reason} Continuando (política: avisar).` });
        }
        return 'ok';
      }
      if (cc.onExceed === 'require_approval') {
        const awaitingEvent: RunEvent = {
          nodeId: node.id,
          iteration,
          status: 'awaiting_human',
          message: `💰 ${reason} Aprovar continuação?`,
          timestamp: nowIso(),
        };
        events.push(awaitingEvent);
        controls.onEvent(awaitingEvent);
        const decision = controls.requestHumanDecision
          ? await controls.requestHumanDecision(node, awaitingEvent)
          : 'approve';
        if (decision === 'reject') {
          emit({ nodeId: node.id, iteration, status: 'error', message: 'Execução interrompida — custo não aprovado.' });
          return 'blocked';
        }
        costApproved = true;
        emit({ nodeId: node.id, iteration, status: 'running', message: 'Custo aprovado pelo responsável — continuando.' });
        return 'ok';
      }
      // block
      emit({ nodeId: node.id, iteration, status: 'error', message: `🛑 ${reason} Execução bloqueada (política: bloquear).` });
      return 'blocked';
    }

    // Executa um único nó (pending → running → done/awaiting_human).
    async function runNode(node: FlowNode, iteration?: number): Promise<'ok' | 'aborted'> {
      if (aborted()) return 'aborted';
      await pauseGate();

      emit({ nodeId: node.id, iteration, status: 'pending', message: 'Na fila…' });
      await delay(stepMs * 0.35);
      if (aborted()) return 'aborted';

      // HITL: pausa em nós de revisão aguardando decisão humana.
      if (node.kind === 'human_review' && effectiveMode(flow, node) === 'in_the_loop') {
        const awaitingEvent: RunEvent = {
          nodeId: node.id,
          iteration,
          status: 'awaiting_human',
          message: (node.data.message as string) ?? 'Aguardando decisão humana…',
          timestamp: nowIso(),
        };
        events.push(awaitingEvent);
        controls.onEvent(awaitingEvent);
        const decision = controls.requestHumanDecision
          ? await controls.requestHumanDecision(node, awaitingEvent)
          : 'approve';
        if (decision === 'reject') {
          emit({ nodeId: node.id, iteration, status: 'error', message: 'Rejeitado pelo revisor humano.' });
          return 'aborted';
        }
        emit({
          nodeId: node.id,
          iteration,
          status: 'done',
          message: decision === 'edit' ? 'Saída editada e aprovada pelo humano.' : 'Aprovado pelo humano.',
        });
        return 'ok';
      }

      emit({ nodeId: node.id, iteration, status: 'running', message: nodeMessage(node) });
      await delay(stepMs);
      if (aborted()) return 'aborted';
      emit({ nodeId: node.id, iteration, status: 'done', message: 'Concluído.' });
      // Cobra o custo do nó e aplica o limite.
      if ((await chargeAndEnforce(node, iteration)) === 'blocked') return 'aborted';
      return 'ok';
    }

    let failed = false;

    // Pré-checagem: budget mensal já esgotado + política de bloqueio → nem inicia.
    if (cc && cc.monthlyCapBRL > 0 && cc.spentBRL >= cc.monthlyCapBRL && cc.onExceed === 'block') {
      emit({
        nodeId: order[0]?.id ?? flow.id,
        status: 'error',
        message: `🛑 Budget mensal já esgotado (R$ ${cc.spentBRL.toFixed(2)} / R$ ${cc.monthlyCapBRL}). Execução bloqueada.`,
      });
      failed = true;
    }

    if (!failed) outer: for (const node of order) {
      if (handled.has(node.id)) continue;
      if (aborted()) {
        failed = true;
        break;
      }

      if (node.kind === 'loop') {
        const body = computeLoopBody(flow.nodes, flow.edges, node.id);
        const maxIter = Math.max(1, Number(node.data.maxIterations) || 3);
        const parallel = Boolean(node.data.parallel);

        emit({ nodeId: node.id, status: 'running', message: parallel ? 'Loop paralelo iniciado.' : 'Loop iniciado.' });
        await delay(stepMs * 0.5);

        const bodyNodes = order.filter((n) => body.has(n.id));
        for (let i = 1; i <= maxIter; i++) {
          if (aborted()) {
            failed = true;
            break outer;
          }
          emit({ nodeId: node.id, iteration: i, status: 'running', message: `Iteração ${i}/${maxIter}` });
          if (parallel) {
            const results = await Promise.all(bodyNodes.map((bn) => runNode(bn, i)));
            if (results.includes('aborted')) {
              failed = true;
              break outer;
            }
          } else {
            for (const bn of bodyNodes) {
              const res = await runNode(bn, i);
              if (res === 'aborted') {
                failed = true;
                break outer;
              }
            }
          }
        }
        emit({ nodeId: node.id, status: 'done', message: `Loop concluído (${maxIter} iterações).` });
        handled.add(node.id);
        bodyNodes.forEach((bn) => handled.add(bn.id));
        continue;
      }

      const res = await runNode(node);
      handled.add(node.id);
      if (res === 'aborted') {
        failed = true;
        break;
      }
    }

    // Artefatos produzidos (mock) — só em execução bem-sucedida.
    const artifacts: Artifact[] = failed ? [] : buildArtifacts(flow, runId);

    const run: Run = {
      id: runId,
      flowId: flow.id,
      flowName: flow.name,
      status: aborted() ? 'error' : failed ? 'error' : 'success',
      startedAt,
      finishedAt: nowIso(),
      durationMs: events.length * stepMs,
      costBRL: runCost,
      events,
      artifacts,
    };
    // Persiste o resultado da execução no backend (best-effort — não quebra a UX).
    try {
      await api.post('/runs', run);
    } catch (err) {
      console.error('Falha ao persistir execução:', err);
    }
    return clone(run);
  },
};

function buildArtifacts(flow: Flow, runId: string): Artifact[] {
  const hasOutput = flow.nodes.some((n) => n.kind === 'output');
  if (!hasOutput) return [];
  const outFormat =
    (flow.nodes.find((n) => n.kind === 'output')?.data.format as string) ?? 'text';
  const ts = nowIso();
  if (outFormat === 'json') {
    return [
      {
        id: uid('art'),
        name: 'resultado.json',
        type: 'json',
        version: 1,
        content: '{\n  "status": "ok",\n  "processados": 3,\n  "geradoEm": "' + ts + '"\n}',
        sourceRunId: runId,
        createdAt: ts,
      },
    ];
  }
  return [
    {
      id: uid('art'),
      name: 'resultado.md',
      type: 'document',
      version: 1,
      content:
        `# Resultado da execução\n\nFluxo **${flow.name}** concluído com sucesso.\n\n- Nós executados: ${flow.nodes.length}\n- Modo de supervisão: ${flow.oversightMode}\n`,
      sourceRunId: runId,
      createdAt: ts,
    },
  ];
}
